import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { Collection} from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { StatisticalCode } from '@sysarb/sysarb-types/general/types';
import type { IsDraft, Job, JobProfileEmployment } from '@sysarb/sysarb-types-v2/services/v1/job';
import { queryOptions } from '@tanstack/vue-query';
import type { QueryOptions } from '@tanstack/vue-query';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);

type JobProfileOptions = {
    queries: {
        getJobProfiles: (subscribe?: boolean) => QueryOptions<Collection<Job> | null, Error, Job[]>,
        getJobProfile: (jobId: string | null, subscribe?: boolean) => QueryOptions<Job | null, Error, Job>,
        getJobProfilePositions: (jobId: string | null, payload: IsDraft, subscribe?: boolean) => QueryOptions<string[] | null, Error>,
        getJobProfileStatisticalCodes: (jobId: string | null, payload: IsDraft, subscribe?: boolean) => QueryOptions<StatisticalCode[] | null, Error>,
        getJobProfileEmployments: (jobId: string | null, payload: IsDraft) => QueryOptions<JobProfileEmployment[] | null, Error>,
    }
};

export const jobProfiles: JobProfileOptions = {
    queries: {
        getJobProfiles: (subscribe: boolean = true): QueryOptions<Collection<Job> | null, Error, Job[]> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.jobs`;
            return queryOptions<Collection<Job> | null, Error, Job[]>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['jobs', topic],
                queryFn: async (): Promise<Collection<Job> | null> => {
                    return ResgateManager.getInstance().collection<Job>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                select: (data) => data?.list ?? [],
            });
        },
        getJobProfile: (jobId: string | null, subscribe: boolean = true): QueryOptions<Job | null, Error, Job> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.job.${jobId}`;
            return queryOptions<Job | null, Error, Job>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic],
                queryFn: async (): Promise<Job | null> => {
                    return ResgateManager.model<Job>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () => jobId != null,
            });
        },
        getJobProfilePositions: (jobId: string | null, payload: IsDraft, subscribe: boolean = true): QueryOptions<string[] | null, Error, string[]> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.job.${jobId}`;
            return queryOptions<string[] | null, Error, string[]>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic, 'positions', payload],
                queryFn: async (): Promise<string[] | null> => {
                    return ResgateManager.getInstance().call<string[]>(topic, 'getpositions', payload).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () => jobId != null,
            });
        },
        getJobProfileStatisticalCodes: (jobId: string | null, payload: IsDraft, subscribe: boolean = true): QueryOptions<StatisticalCode[] | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.job.${jobId}`;
            return queryOptions<StatisticalCode[] | null, Error>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic, 'statisticalcodes', payload],
                queryFn: async (): Promise<StatisticalCode[] | null> => {
                    return ResgateManager.getInstance().call<StatisticalCode[]>(topic, 'getstatisticalcodes', payload).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () =>  jobId != null,
            });
        },
        getJobProfileEmployments: (jobId: string | null, payload: IsDraft): QueryOptions<JobProfileEmployment[] | null, Error> => {
            const { instance } = useLoginStore();
            const url = `v1/${instance}/job/${jobId}/getemployments`;
            return queryOptions<JobProfileEmployment[] | null, Error>({
                queryKey: [url, instance, jobId, payload],
                queryFn: async (): Promise<JobProfileEmployment[] | null> => {
                    return jobService.post<JobProfileEmployment[]>(payload, url);
                },
                enabled: () => jobId != null,
            });
        },
    },
};
